import { addCommaToNumber, ModalConfirm } from '@you-go-vn/components';
import useDebounce from '@you-go-vn/components/hooks/useDebounce';
import FreeShipIcon from '@you-go-vn/components/src/Common/Icons/FreeShipIcon';
import SaleIcon from '@you-go-vn/components/src/Common/Icons/SaleIcon';
import LogoIcon from '@you-go-vn/components/src/Common/Icons/LogoIcon';

import {
  Button,
  message,
  Space,
  Table,
  TableColumnsType,
  TablePaginationConfig,
  Tooltip,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { promotionDiscountTargets, promotionTypes } from '../..';
import { useAppSelector } from '../../../../store';
import { PromotionDetail } from '../../data';
import { yougoPromotionServices } from '../services';
import { ImportOutlined } from '@ant-design/icons';
import DetailModal from '../../components/DetailModal';

const initPagination: TablePaginationConfig = {
  current: 1,
  pageSize: 10,
  total: 0,
};

type Props = {
  searchInput: string;
};

export default function TabCanJoin({ searchInput }: Props) {
  const { currentRestaurant } = useAppSelector((s) => s.restaurant);
  const [data, setData] = useState<PromotionDetail[]>([]);
  const [pagination, setPagination] = useState(initPagination);

  const [loadingTable, setLoadingTable] = useState(true);

  const [currentRow, setCurrentRow] = useState<PromotionDetail>();

  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const [joinModalVisible, setJoinModalVisible] = useState(false);
  const [joinConfirmLoading, setJoinConfirmLoading] = useState(false);

  const fetchTable = useCallback(
    ({ pagination = initPagination, value = '' }) => {
      setLoadingTable(true);
      yougoPromotionServices
        .getYougoPromotionsRestaurantCanJoin(currentRestaurant?.id, {
          page: pagination.current,
          limit: pagination.pageSize,
          keywords: value ? value : undefined,
        })
        .then(async ({ data }) => {
          if (data.data) {
            const items = data.data.items;
            setData(items);
            setPagination({
              ...pagination,
              total: data.data.total,
            });
            setLoadingTable(false);
          }
        });
    },
    [currentRestaurant],
  );

  const search = useDebounce(fetchTable);

  useEffect(() => {
    search(searchInput);
  }, [search, searchInput]);

  const ActionGroup = ({ record }: { record?: PromotionDetail }) => {
    if (!record) return <></>;
    return (
      <Space wrap={false}>
        <Button
          key="join"
          className="light action"
          block
          size="small"
          icon={<ImportOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setJoinModalVisible(true);
            setCurrentRow(record);
          }}
        >
          Tham gia
        </Button>
      </Space>
    );
  };

  const columns: TableColumnsType<PromotionDetail> = [
    {
      title: 'Tên',
      dataIndex: 'name',
      render: (_, record) => (
        <Space>
          <Tooltip
            title={
              promotionTypes.find((x) => x.value === record.type)?.label ||
              'Chưa cập nhật'
            }
          >
            <div>
              {record.type === 'yougofood' && (
                <LogoIcon style={{ fontSize: 40 }} />
              )}
              {record.type === 'yougobike' && 'YouGo Bike'}
              {record.type === 'yougoship' && 'YouGo Ship'}
            </div>
          </Tooltip>
          <span>{record.name}</span>
        </Space>
      ),
    },
    {
      title: 'Mã khuyến mãi',
      dataIndex: 'code',
      // responsive: ['xl'],
    },
    { title: 'Mô tả', dataIndex: 'description', responsive: ['xl'] },
    {
      title: 'Giảm giá',
      align: 'right',
      // responsive: ['lg'],
      render: (_, record) => (
        <Space>
          <span>
            {addCommaToNumber(record.discountAmount)}
            {record.discountType === 'fixed_amount' ? ' VNĐ' : ' %'}
          </span>
          <Tooltip
            title={
              promotionDiscountTargets.find(
                (x) => x.value === record.discountTarget,
              )?.label || 'Chưa cập nhật'
            }
          >
            {record.discountTarget === 'base_total' ? (
              <SaleIcon style={{ fontSize: 40, color: 'red' }} />
            ) : (
              <FreeShipIcon style={{ fontSize: 40, color: 'yellowgreen' }} />
            )}
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Tuỳ chọn',
      dataIndex: 'option',
      responsive: ['lg'],
      align: 'right',
      width: 250,
      render: (_, record) => <ActionGroup record={record} />,
    },
  ];

  return (
    <>
      <Table<PromotionDetail>
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data}
        pagination={pagination}
        loading={loadingTable}
        onChange={(pagination) => {
          fetchTable({ pagination, value: searchInput });
        }}
        onRow={(record) => {
          return {
            onClick: () => {
              setCurrentRow(record);
              setDetailModalVisible(true);
            },
          };
        }}
      />
      {currentRow && (
        <DetailModal
          visible={detailModalVisible}
          initialValues={currentRow}
          onCancel={() => {
            setDetailModalVisible(false);
            setCurrentRow(undefined);
          }}
          actions={<ActionGroup record={currentRow} />}
        />
      )}

      <ModalConfirm
        type="action"
        confirm
        confirmLoading={joinConfirmLoading}
        title={`Tham gia chương trình khuyến mãi "${currentRow?.name}"`}
        icon={<ImportOutlined />}
        visible={joinModalVisible}
        onConfirm={async () => {
          if (!currentRow) return;
          setJoinConfirmLoading(true);
          try {
            await yougoPromotionServices.joinYougoPromotions(
              currentRestaurant?.id,
              currentRow.id,
            );
            message.success('Thành công');
            setJoinModalVisible(false);
            setCurrentRow(undefined);
            fetchTable({});
          } catch (error) {
            message.error('Đã xảy ra lỗi, vui lòng kiểm tra lại!');
          }
          setJoinConfirmLoading(false);
        }}
        onCancel={() => {
          setJoinModalVisible(false);
          !detailModalVisible && setCurrentRow(undefined);
        }}
      />
    </>
  );
}
